<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
      <p class="text-sm font-semibold text-gray-900 dark:text-white">By Day</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Breakdown per weekday</p>
    </div>

    <!-- Skeleton -->
    <template v-if="loading && !chartData">
      <div class="animate-pulse divide-y divide-gray-50 dark:divide-gray-800/60">
        <div v-for="i in 7" :key="i" class="flex items-center justify-between px-4 py-2.5">
          <div class="flex items-center gap-2">
            <div class="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
          </div>
          <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-8"></div>
        </div>
      </div>
    </template>

    <!-- Empty -->
    <div v-else-if="isEmpty" class="flex flex-col items-center justify-center py-12 px-4 text-center">
      <p class="text-sm text-gray-400 dark:text-gray-500">No bookings for this period.</p>
    </div>

    <!-- Table -->
    <template v-else>
      <div class="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-700">
        <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Day</span>
        <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Bookings</span>
      </div>
      <div class="max-h-[340px] overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800/60">
        <div v-for="row in rows" :key="row.name">
          <button type="button" @click="row.value > 0 && toggle(row.name)"
            class="w-full flex items-center justify-between px-4 py-2.5 transition-colors text-left"
            :class="row.value > 0 ? 'hover:bg-gray-50 dark:hover:bg-gray-800/50' : 'cursor-default'">
            <div class="flex items-center gap-2">
              <img :src="`/images/weekdays_icons/${row.weekday}.svg`" class="w-5 h-5 flex-shrink-0" :alt="row.name" />
              <span class="text-sm font-medium text-gray-800 dark:text-gray-200">{{ row.name }}</span>
            </div>
            <div class="flex items-center gap-2">
              <span class="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{{ row.value }}</span>
              <svg v-if="row.value > 0"
                class="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 transition-transform duration-150"
                :class="expanded.has(row.name) ? 'rotate-90' : ''"
                fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
              </svg>
            </div>
          </button>

          <!-- Drilldown dates -->
          <div v-if="expanded.has(row.name)" class="bg-gray-50/60 dark:bg-gray-800/20 divide-y divide-gray-100 dark:divide-gray-700/40">
            <div v-for="[date, count] in drillEntries(row.name)" :key="date"
              class="flex items-center justify-between px-4 py-2 pl-9">
              <div class="flex items-center gap-1.5">
                <svg class="w-3.5 h-3.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"/>
                </svg>
                <span class="text-xs text-gray-600 dark:text-gray-300">{{ date }}</span>
              </div>
              <span class="text-xs font-medium text-gray-600 dark:text-gray-300 tabular-nums">{{ count }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useAnalyticsBookingsStore } from 'stores/analyticsBookings.js'

const props = defineProps({ duration: { type: String, required: true } })
const store = useAnalyticsBookingsStore()

const loading   = computed(() => store.popularDaysLoading)
const chartData = computed(() => store.popularDays[props.duration] ?? null)

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const rows = computed(() => {
  const series = chartData.value?.series?.[0]?.data ?? []
  return series.map((item, i) => ({
    name:    item.name,
    value:   item.y,
    weekday: WEEKDAYS[i % 7],
  }))
})

const isEmpty = computed(() => rows.value.length === 0 || rows.value.every(r => r.value === 0))

const expanded = ref(new Set())

function toggle(name) {
  const s = new Set(expanded.value)
  s.has(name) ? s.delete(name) : s.add(name)
  expanded.value = s
}

function drillEntries(dayName) {
  const ds = chartData.value?.drilldown?.series ?? []
  const match = ds.find(s => s.id === dayName)
  return (match?.data ?? []).slice().sort((a, b) => b[1] - a[1])
}
</script>
